import React from 'react';
import { Badge, Flex } from '@/components/ui';
import { IconCheck } from '@tabler/icons-react';
import { FieldColumnSelector } from '../FieldColumnSelector';
import type { ColumnMapping, FormField, SelectedFile, StudyForm } from '../types';

interface FormSelectorFieldsProps {
  selectedForm: StudyForm;
  file?: SelectedFile;
  fileId?: string;
  columnMappings?: ColumnMapping[];
  disableColumns?: boolean;
  useNewSelector?: boolean;
  hoveredElement?: {
    type: 'column' | 'field' | 'metadata';
    name: string;
  } | null;
  onElementHover?: (
    element: { type: 'column' | 'field' | 'metadata'; name: string } | null
  ) => void;
  getElementOpacity?: (
    elementType: 'column' | 'field' | 'metadata',
    elementName: string
  ) => number;
  onFieldClick?: (fieldDataPoint: string) => void;
  onFieldColumnMappingChange?: (
    fileId: string,
    columnName: string,
    mappingType: 'formField' | 'metadata' | 'ignored',
    targetField?: string
  ) => void;
}

export const FormSelectorFields: React.FC<FormSelectorFieldsProps> = ({
  selectedForm,
  file,
  fileId,
  columnMappings,
  disableColumns,
  useNewSelector = false,
  onElementHover,
  getElementOpacity,
  onFieldClick,
  onFieldColumnMappingChange,
}) => {
  // Check if a field has explicit mapping
  const hasExplicitMapping = (fieldDataPoint: string) => {
    if (!columnMappings || !fileId) return false;
    return columnMappings.some(
      mapping =>
        mapping.fileId === fileId &&
        mapping.mappingType === 'formField' &&
        mapping.targetField === fieldDataPoint
    );
  };

  // Calculate matching columns
  const matchingColumns = React.useMemo(() => {
    if (!selectedForm || !file || !selectedForm.fields) return [];
    const fileColumns = file.metadata.columns;
    const formFields = selectedForm.fields
      .filter(field => field && field.dataPoint)
      .map(field => field.dataPoint);
    return fileColumns.filter(col =>
      formFields.some(field => field.toLowerCase() === col.toLowerCase())
    );
  }, [selectedForm, file]);

  // Check if a field has a matching unmapped column
  const hasMatchingUnmappedColumn = (fieldDataPoint: string) => {
    if (!columnMappings || !fileId) return false;
    const matchingColumn = matchingColumns.find(
      col => col.toLowerCase() === fieldDataPoint.toLowerCase()
    );
    if (!matchingColumn) return false;
    return !columnMappings.some(
      mapping =>
        mapping.fileId === fileId && mapping.columnName === matchingColumn
    );
  };

  if (disableColumns || !selectedForm.fields) return null;

  return (
    <Flex gap="1" wrap="wrap">
      {selectedForm.fields
        .filter(field => field && field.dataPoint)
        .map((field: FormField, index: number) => {
          const fieldExplicitlyMapped = hasExplicitMapping(field.dataPoint);
          const fieldHasMatchingUnmapped = hasMatchingUnmappedColumn(
            field.dataPoint
          );

          // Use new FieldColumnSelector when enabled
          if (
            useNewSelector &&
            file &&
            fileId &&
            onFieldColumnMappingChange
          ) {
            return (
              <div
                key={index}
                style={{
                  transition: 'all 0.2s ease',
                  opacity: getElementOpacity
                    ? getElementOpacity('field', field.dataPoint)
                    : 1,
                }}
                onMouseEnter={() =>
                  onElementHover &&
                  onElementHover({ type: 'field', name: field.dataPoint })
                }
                onMouseLeave={() => onElementHover && onElementHover(null)}
              >
                <FieldColumnSelector
                  field={field}
                  fileColumns={file.metadata.columns}
                  fileMappings={columnMappings || []}
                  fileId={fileId}
                  onMappingChange={onFieldColumnMappingChange}
                  triggerLabel={field.dataPoint}
                />
              </div>
            );
          }

          return (
            <Badge
              key={index}
              color={
                fieldExplicitlyMapped
                  ? 'success'
                  : fieldHasMatchingUnmapped
                    ? 'success'
                    : 'warning'
              }
              variant={fieldExplicitlyMapped ? 'surface' : 'dashed'}
              size="small"
              startIcon={
                fieldExplicitlyMapped || fieldHasMatchingUnmapped ? (
                  <IconCheck />
                ) : undefined
              }
              style={{
                transition: 'all 0.2s ease',
                opacity: getElementOpacity
                  ? getElementOpacity('field', field.dataPoint)
                  : 1,
                cursor: onFieldClick ? 'pointer' : 'default',
              }}
              onMouseEnter={() =>
                onElementHover &&
                onElementHover({ type: 'field', name: field.dataPoint })
              }
              onMouseLeave={() => onElementHover && onElementHover(null)}
              onClick={() =>
                onFieldClick && onFieldClick(field.dataPoint)
              }
            >
              {field.dataPoint}
            </Badge>
          );
        })}
    </Flex>
  );
};

